import {action, observable, makeObservable} from "mobx";
import FieldInfoFormat from "@arcgis/core/popup/support/FieldInfoFormat";

export interface PopupPanelInfo {
    graphics: __esri.Graphic[];
    title: string;
    index?: number;
    order?: number;
}

export class PopupStore {
    disabled: boolean;
    selected: string | undefined;
    visible: boolean;
    selectedPanelIndex: number;
    activeIndex: number[];
    activePages: number[];
    lastItemTitle: string = "";
    valueUnitMap: Map<string, Map<string, string>> = new Map();
    defaultFormatMap: Map<string, Map<string, FieldInfoFormat>> = new Map();
    panels: PopupPanelInfo[] = [];
    panelIndex: number = 1;
    features: __esri.Graphic[];
    mapPoint?: __esri.Point;
    timeoutMap: Map<string, number> = new Map();
    peparedFields: string[] = [];
    constructor() {
        // set before making observable
        this.setSelectedPanelIndex(1);
        this.setActiveIndex([1]);
        this.setActivePages([0, 1]);
        this.setFeatures([]);

        makeObservable(this, {
            disabled: observable,
            selected: observable,
            visible: observable,
            selectedPanelIndex: observable,
            activeIndex: observable,
            activePages: observable,
            lastItemTitle: observable,
            panels: observable,
            panelIndex: observable,
            features: observable,
            mapPoint: observable,

            setDisabled: action,
            setSelected: action,
            setVisible: action,
            setSelectedPanelIndex: action,
            setActiveIndex: action,
            setActivePage: action,
            setActivePages: action,
            setLastItemTitle: action,
            addFeatures: action,
            setFeatures: action,
            setMapPoint: action,
            addPanel: action,
            clearPanels: action
        });
        this.setDisabled(false);
        this.setVisible(false);
    }
    setDisabled(disabled: boolean) {
        this.disabled = disabled;
    }
    setSelected(selected?: string) {
        this.selected = selected;
    }
    setVisible(visible: boolean) {
        this.visible = visible;
        if(!visible){
            this.setSelected();
        }
    }
    setSelectedPanelIndex(index: number) {
        this.selectedPanelIndex = index;
    }
    setActiveIndex(index: number[]) {
        this.activeIndex = index;
    }
    setActivePage(panelIndex: number, page: number) {
        this.activePages[panelIndex] = page;
    }
    getActivePage(panelIndex: number) {
        return this.activePages[panelIndex];
    }
    setActivePages(index: number[]) {
        this.activePages = index;
    }
    setLastItemTitle(title: string) {
        this.lastItemTitle = title;
    }
    addFeatures(features: __esri.Graphic | __esri.Graphic[]){
        if(!Array.isArray(features)){
            features = [features];
        }
        this.features = this.features.concat(features);
    }
    setFeatures(features: __esri.Graphic[]){
        this.features = features;
    }
    setMapPoint(mapPoint?: __esri.Point) {
        this.mapPoint = mapPoint;
    }
    addPanel(panel: PopupPanelInfo) {
        panel.index = this.panelIndex;
        if(typeof panel.order === "undefined"){
            panel.order = 0;
        }
        this.panels.push(panel);
        this.panels.sort((a, b) => b.order - a.order || a.index - b.index).map((p, index) => {
            p.index = index + 1;
        });
        this.panelIndex++;
    }
    setTimeoutID(key: string, timeoutID: number){
        this.clearTimeout(key);
        this.timeoutMap.set(key, timeoutID);
    }
    getTimeoutID(key: string){
        return this.timeoutMap.get(key);
    }
    clearTimeout(key: string){
        const timeoutID = this.getTimeoutID(key);
        if(timeoutID){
            clearTimeout(timeoutID);
        }
        this.timeoutMap.delete(key);
    }
    clearTimeouts(){
        const keys = this.timeoutMap.keys();
        for (const key of keys) {
            this.clearTimeout(key);
        }
        this.timeoutMap.clear();
    }
    clearPanels() {
        this.panels = [];
        this.panelIndex = 1;
    }
}
